import { resolveReferenceFieldValue } from "./auditReferenceResolver";

const GLOBAL_NOISE_FIELDS = new Set(["updatedAt", "createdAt", "deletedAt"]);

const ENTITY_OMIT_FIELDS = {
  time_table: new Set(["generationStats", "fitnessScore"]),
  timetable: new Set(["generationStats", "fitnessScore"]),
  timetable_slot: new Set(),
  time_table_slot: new Set(),
};

const STATUS_LABELS = {
  draft: "Draft",
  active: "Active",
  locked: "Locked",
  archived: "Archived",
};

const FIELD_LABELS = {
  status: "Status",
  isActive: "Live timetable",
  name: "Name",
  title: "Title",
  code: "Code",
  academicYear: "Academic year",
  lockedAt: "Locked at",
  generationMode: "Generation mode",
  generationType: "Event",
  slotCount: "Slots created",
  keptSlotCount: "Slots kept from previous",
  algorithmGenerations: "Algorithm iterations",
  quality: "Quality score",
  filters: "Filters used",
  schedule: "Schedule settings",
  otherTimetablesDeactivated: "Other timetables deactivated",
  hardViolations: "Hard rule violations",
  fitnessScore: "Fitness score",
  classId: "Class",
  courseId: "Course",
  instituteId: "Institute",
  departmentId: "Department",
  credits: "Credits",
  semester: "Semester",
  type: "Type",
  hoursPerWeek: "Hours per week",
  totalHours: "Total hours",
  consecutiveHoursAllowed: "Consecutive hours allowed",
  requiresLab: "Requires lab",
  email: "Email",
  fullName: "Full name",
  firstName: "First name",
  lastName: "Last name",
  phone: "Phone",
  role: "Role",
  _summary: "Note",
  _relatedUpdates: "Additional details",
};

const HIDDEN_DETAIL_FIELDS = new Set([
  "id",
  "uuid",
  "instituteId",
  "isDeleted",
  "deletedAt",
  "createdAt",
  "updatedAt",
  "userId",
  "teacherId",
  "departmentId",
  "password",
  "passwordHash",
  "refreshToken",
]);

const ENTITY_DETAIL_FIELDS = {
  subject: [
    "name",
    "code",
    "courseId",
    "credits",
    "semester",
    "type",
    "hoursPerWeek",
    "totalHours",
    "consecutiveHoursAllowed",
    "requiresLab",
    "status",
  ],
  user: ["fullName", "firstName", "lastName", "email", "phone", "role"],
  teacher: ["fullName", "firstName", "lastName", "email", "phone", "employeeId"],
  student: ["fullName", "firstName", "lastName", "email", "phone", "studentLoginID"],
  class: ["name", "section", "academicYear", "status"],
  course: ["name", "code", "duration", "status"],
  timetable: [
    "generationType",
    "name",
    "academicYear",
    "slotCount",
    "keptSlotCount",
    "fitnessScore",
    "generationMode",
    "schedule",
    "filters",
    "quality",
    "algorithmGenerations",
    "hardViolations",
    "previousTimetableId",
    "otherTimetablesDeactivated",
    "status",
    "isActive",
  ],
  time_table: [
    "generationType",
    "name",
    "academicYear",
    "slotCount",
    "keptSlotCount",
    "fitnessScore",
    "generationMode",
    "schedule",
    "filters",
    "quality",
    "algorithmGenerations",
    "hardViolations",
    "previousTimetableId",
    "otherTimetablesDeactivated",
    "status",
    "isActive",
  ],
  assignment: ["title", "dueDate", "status", "maxMarks"],
  exam: ["name", "examDate", "status"],
};

export const normalizeAuditValue = (value) => {
  if (value == null) return null;
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  const looksLikeJsonObject = trimmed.startsWith("{") && trimmed.endsWith("}");
  const looksLikeJsonArray = trimmed.startsWith("[") && trimmed.endsWith("]");

  if (!looksLikeJsonObject && !looksLikeJsonArray) return value;

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const valuesEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const getOmitFields = (entityType) => {
  const key = String(entityType || "").trim().toLowerCase();
  return ENTITY_OMIT_FIELDS[key] || new Set();
};

export function formatAuditFieldLabel(field) {
  if (!field) return "Field";
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  return field
    .replace(/([A-Z])/g, " $1")
    .replace(/[._[\]]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}

export function formatAuditFieldValue(value, field = "", { emptyLabel = "—", references } = {}) {
  if (value == null || value === "") return emptyLabel;

  const resolvedReference = resolveReferenceFieldValue(value, field, references);
  if (resolvedReference) return resolvedReference;

  if (field === "status") {
    if (typeof value === "boolean") return value ? "Active" : "Inactive";
    if (typeof value === "string") return STATUS_LABELS[value.toLowerCase()] || value;
  }

  if (field === "isActive" && typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (field === "schedule" && typeof value === "object" && value !== null) {
    const parts = [];
    if (value.dayStart && value.dayEnd) parts.push(`${value.dayStart} – ${value.dayEnd}`);
    if (value.slotDurationMinutes != null) parts.push(`${value.slotDurationMinutes} min slots`);
    if (value.breakCount != null) parts.push(`${value.breakCount} breaks`);
    if (parts.length) return parts.join(", ");
  }

  if (field === "filters" && typeof value === "object" && value !== null) {
    const parts = Object.entries(value)
      .filter(([, v]) => v != null && v !== "")
      .map(([key, val]) => {
        const resolved = resolveReferenceFieldValue(val, key, references);
        const display = resolved || (Array.isArray(val) ? val.join(", ") : String(val));
        return `${formatAuditFieldLabel(key)}: ${display}`;
      });
    if (parts.length) return parts.join("; ");
  }

  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (typeof value === "string") {
    if (value === "true") return "Yes";
    if (value === "false") return "No";
    if (field.toLowerCase().includes("status")) {
      return STATUS_LABELS[value.toLowerCase()] || value.charAt(0).toUpperCase() + value.slice(1);
    }
    return value;
  }

  if (typeof value === "number") return value.toLocaleString();

  if (Array.isArray(value)) {
    if (value.length === 0) return "No items";
    if (value.length <= 3) return value.map((item) => formatAuditFieldValue(item)).join(", ");
    return `${value.length} items`;
  }

  if (typeof value === "object") {
    if (value._summary) return value._summary;
    const keys = Object.keys(value);
    if (keys.length === 0) return "Empty";
    if (keys.length <= 3) {
      return keys
        .map((key) => `${formatAuditFieldLabel(key)}: ${formatAuditFieldValue(value[key], key)}`)
        .join(", ");
    }
    return `${keys.length} properties updated`;
  }

  return String(value);
}

function hasDisplayableValue(value, field) {
  if (value == null || value === "") return false;
  if (HIDDEN_DETAIL_FIELDS.has(field)) return false;
  if (typeof value === "object" && !Array.isArray(value)) {
    return Object.keys(value).length > 0;
  }
  return true;
}

function getEntityDetailFieldList(entityType, sourceKeys) {
  const entityKey = String(entityType || "").trim().toLowerCase();
  const whitelist = ENTITY_DETAIL_FIELDS[entityKey];
  if (whitelist) {
    return whitelist.filter((key) => sourceKeys.includes(key));
  }
  return sortDetailFields(
    sourceKeys.filter((key) => !GLOBAL_NOISE_FIELDS.has(key) && !HIDDEN_DETAIL_FIELDS.has(key)),
  );
}

function buildDetailRows(source, entityType, { valueKey }) {
  if (!source || typeof source !== "object" || Array.isArray(source)) return [];

  const omit = getOmitFields(entityType);
  const sourceKeys = Object.keys(source).filter((key) => !omit.has(key));
  const orderedKeys = getEntityDetailFieldList(entityType, sourceKeys);

  return orderedKeys
    .filter((key) => hasDisplayableValue(source[key], key))
    .map((key) => ({
      field: key,
      fieldLabel: formatAuditFieldLabel(key),
      displayValue: source[key],
      oldFieldValue: valueKey === "old" ? source[key] : null,
      newFieldValue: valueKey === "new" ? source[key] : null,
      changed: true,
    }));
}

/**
 * Top-level changed fields only — avoids noisy nested flattening (e.g. generationStats.*).
 */
export function getMeaningfulDiffs(oldValue, newValue, entityType) {
  const oldNorm = normalizeAuditValue(oldValue);
  const newNorm = normalizeAuditValue(newValue);
  const omit = getOmitFields(entityType);

  if (newNorm?._summary && (!oldNorm || Object.keys(oldNorm).length === 0)) {
    return [
      {
        field: "_summary",
        fieldLabel: formatAuditFieldLabel("_summary"),
        oldFieldValue: null,
        newFieldValue: newNorm._summary,
        changed: true,
      },
    ];
  }

  const oldPlain = oldNorm && typeof oldNorm === "object" && !Array.isArray(oldNorm) ? oldNorm : {};
  const newPlain = newNorm && typeof newNorm === "object" && !Array.isArray(newNorm) ? newNorm : {};
  const keys = new Set([...Object.keys(oldPlain), ...Object.keys(newPlain)]);
  const diffs = [];

  for (const key of keys) {
    if (GLOBAL_NOISE_FIELDS.has(key) || omit.has(key)) continue;

    const oldFieldValue = oldPlain[key];
    const newFieldValue = newPlain[key];
    const changed = !valuesEqual(oldFieldValue, newFieldValue);
    if (!changed) continue;

    diffs.push({
      field: key,
      fieldLabel: formatAuditFieldLabel(key),
      oldFieldValue,
      newFieldValue,
      changed: true,
    });
  }

  if (newPlain._relatedUpdates) {
    diffs.push({
      field: "_relatedUpdates",
      fieldLabel: formatAuditFieldLabel("_relatedUpdates"),
      oldFieldValue: null,
      newFieldValue: newPlain._relatedUpdates,
      changed: true,
    });
  }

  if (diffs.length === 0) {
    const omittedSummaries = {
      generationStats: "Timetable scheduling data was recalculated",
      fitnessScore: "Timetable quality score was updated",
    };

    for (const key of omit) {
      const hadOld = Object.prototype.hasOwnProperty.call(oldPlain, key);
      const hadNew = Object.prototype.hasOwnProperty.call(newPlain, key);
      if (!hadOld && !hadNew) continue;
      if (valuesEqual(oldPlain[key], newPlain[key])) continue;

      diffs.push({
        field: `_omitted_${key}`,
        fieldLabel: "Note",
        oldFieldValue: null,
        newFieldValue: omittedSummaries[key] || `${formatAuditFieldLabel(key)} was updated`,
        changed: true,
        isSummary: true,
      });
    }
  }

  if (diffs.length === 0) {
    const entityKey = String(entityType || "").trim().toLowerCase();
    const focusFields =
      entityKey === "time_table" || entityKey === "timetable"
        ? ["status", "isActive", "name", "academicYear", "lockedAt", "generationMode"]
        : ["status", "isActive", "name", "title"];

    for (const key of focusFields) {
      if (!valuesEqual(oldPlain[key], newPlain[key])) {
        diffs.push({
          field: key,
          fieldLabel: formatAuditFieldLabel(key),
          oldFieldValue: oldPlain[key],
          newFieldValue: newPlain[key],
          changed: true,
        });
      }
    }
  }

  return diffs;
}

export function describeAuditChanges(oldValue, newValue, entityType, references) {
  const diffs = getMeaningfulDiffs(oldValue, newValue, entityType);
  if (diffs.length === 0) return null;

  const format = (val, field) => formatAuditFieldValue(val, field, { references });

  if (diffs.length === 1) {
    const diff = diffs[0];
    if (diff.field === "_summary" || diff.field === "_relatedUpdates") {
      return format(diff.newFieldValue, diff.field);
    }
    const from = format(diff.oldFieldValue, diff.field);
    const to = format(diff.newFieldValue, diff.field);
    return `${diff.fieldLabel}: ${from} → ${to}`;
  }

  if (diffs.length <= 3) {
    return diffs
      .filter((diff) => diff.field !== "_relatedUpdates")
      .map((diff) => {
        const from = format(diff.oldFieldValue, diff.field);
        const to = format(diff.newFieldValue, diff.field);
        return `${diff.fieldLabel}: ${from} → ${to}`;
      })
      .join("; ");
  }

  const labels = diffs
    .filter((diff) => diff.field !== "_relatedUpdates")
    .slice(0, 3)
    .map((diff) => diff.fieldLabel);
  const suffix = diffs.length > 3 ? ` +${diffs.length - 3} more` : "";
  return `${labels.join(", ")}${suffix}`;
}

const PRIORITY_DETAIL_FIELDS = [
  "name",
  "title",
  "status",
  "academicYear",
  "email",
  "fullName",
  "firstName",
  "lastName",
  "classId",
  "isActive",
  "generationMode",
  "id",
];

function sortDetailFields(keys) {
  return [...keys].sort((a, b) => {
    const aIndex = PRIORITY_DETAIL_FIELDS.indexOf(a);
    const bIndex = PRIORITY_DETAIL_FIELDS.indexOf(b);
    const aRank = aIndex === -1 ? 999 : aIndex;
    const bRank = bIndex === -1 ? 999 : bIndex;
    if (aRank !== bRank) return aRank - bRank;
    return a.localeCompare(b);
  });
}

export function getAuditDetailDiffs(oldValue, newValue, action, entityType) {
  if (action === "update") {
    return getMeaningfulDiffs(oldValue, newValue, entityType);
  }

  if (action === "create") {
    const source = normalizeAuditValue(newValue);
    return buildDetailRows(source, entityType, { valueKey: "new" });
  }

  if (action === "delete") {
    const source = normalizeAuditValue(oldValue);
    return buildDetailRows(source, entityType, { valueKey: "old" });
  }

  return [];
}

export function getAuditRecordTitle(log) {
  if (!log) return "";
  const source =
    log.action === "delete"
      ? normalizeAuditValue(log.oldValue)
      : normalizeAuditValue(log.newValue);

  if (source && typeof source === "object") {
    const title =
      source.name ||
      source.title ||
      source.fullName ||
      [source.firstName, source.lastName].filter(Boolean).join(" ");
    if (title) return String(title);
  }
  return "";
}
